import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { DOCUMENT_TAGS } from '../../data/domain';
import type { ComplianceDocument } from '../../data/types';
import { nowIso, uid } from '../../lib/format';
import { queueDocument } from '../../sync/outbox';
import { Modal } from '../../ui/Modal';
import { DocumentPreviewBody, useDocumentFile } from './DocumentPreview';
import { isCurrentSdsScan, reviewDateForIssuedDate, scanSdsDate, shouldUpdateAutofilledReviewDate } from './sdsDateScan';

export function DocumentUploadModal({ document, onClose }: { document: ComplianceDocument | null; onClose(): void }) {
  const [name, setName] = useState(document?.documentName || document?.name || '');
  const [tag, setTag] = useState(document?.tag || 'SDS');
  const [issuedDate, setIssuedDate] = useState(document?.issuedDate || '');
  const [expiry, setExpiry] = useState(document?.expiry || '');
  const [reviewDateWasAutoFilled, setReviewDateWasAutoFilled] = useState(document?.reviewDateAutoFilled ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [feedback, setFeedback] = useState<{ text: string; warning?: boolean }>({ text: document?.issuedDate ? `Detected issue date: ${document.issuedDate}.` : '' });
  const fileUrl = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);
  const scanId = useRef(0);
  useEffect(() => () => { if (fileUrl) URL.revokeObjectURL(fileUrl); }, [fileUrl]);

  async function chooseFile(chosen: File | null) {
    const requestId = ++scanId.current;
    setFile(chosen);
    if (!chosen) return;
    if (!name) setName(chosen.name.replace(/\.[^.]+$/, ''));
    if (tag !== 'SDS') return;
    setFeedback({ text: 'Scanning SDS for issued, revised and version dates...' });
    try {
      const found = await scanSdsDate(chosen);
      if (!isCurrentSdsScan(requestId, scanId.current)) return;
      if (!found) { setFeedback({ text: 'No labelled issue, revision or version date found. Set the dates manually.', warning: true }); return; }
      const issued = found.date.toISOString().slice(0, 10);
      const review = reviewDateForIssuedDate(issued);
      setIssuedDate(issued);
      const replacesReviewDate = shouldUpdateAutofilledReviewDate(expiry, reviewDateWasAutoFilled);
      if (replacesReviewDate) {
        setExpiry(review);
        setReviewDateWasAutoFilled(true);
      }
      setFeedback({ text: `Found ${found.label}: ${issued}.${found.statesFiveYearValidity ? ' The SDS states a five-year validity period.' : ''} Review date set to ${replacesReviewDate ? review : expiry}.` });
    } catch {
      setFeedback({ text: 'Could not read a date from this file. Set the dates manually.', warning: true });
    }
  }

  function changeIssued(value: string) {
    scanId.current += 1;
    setIssuedDate(value);
    if (value && shouldUpdateAutofilledReviewDate(expiry, reviewDateWasAutoFilled)) {
      setExpiry(reviewDateForIssuedDate(value));
      setReviewDateWasAutoFilled(true);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!file && !document) return setFeedback({ text: 'Choose a file to upload.', warning: true });
    const now = nowIso();
    const next: ComplianceDocument = {
      id: document?.id ?? uid('doc_'),
      name: file?.name ?? document!.name,
      documentName: name.trim() || file?.name || document!.name,
      tag, expiry, issuedDate, reviewDateAutoFilled: reviewDateWasAutoFilled,
      type: file?.type || document?.type || 'application/octet-stream',
      size: file?.size ?? document?.size ?? 0,
      uploadedAt: document?.uploadedAt ?? now,
      updatedAt: now
    };
    // Queued like every other change, so uploads made without reception go up when the device reconnects.
    await queueDocument(file ? 'upload' : 'update', next, `Compliance ${file ? 'upload' : 'update'}: ${next.documentName}`, file ?? undefined);
    onClose();
  }

  const previewType = file ? file.type : document?.type || '';
  const previewName = file ? file.name : document?.name || '';
  return (
    <Modal id="documentUploadModal" onClose={onClose} labelledBy="documentUploadTitle">
      <form onSubmit={save}>
        <p className="eyebrow">Compliance library</p>
        <h2 id="documentUploadTitle">{document ? 'Review document' : 'Upload document'}</h2>
        <label>Document name <input id="uploadDocumentName" placeholder="e.g. Product safety data sheet" value={name} onChange={(event) => setName(event.target.value)} /></label>
        <div className="form-row">
          <label>Document tag <select id="uploadDocumentTag" value={tag} onChange={(event) => setTag(event.target.value)}>{[...new Set([...DOCUMENT_TAGS, tag])].map((item) => <option key={item}>{item}</option>)}</select></label>
          <label>Issued / revised date <input id="uploadDocumentIssuedDate" type="date" value={issuedDate} onChange={(event) => changeIssued(event.target.value)} /></label>
        </div>
        <label>Expiry / review date <input id="uploadDocumentExpiry" type="date" value={expiry} onChange={(event) => { scanId.current += 1; setExpiry(event.target.value); setReviewDateWasAutoFilled(false); }} /></label>
        <label>{document ? 'Replace file (optional)' : 'File'} <input id="documentUploadInput" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(event) => chooseFile(event.target.files?.[0] ?? null)} /></label>
        {(file || document) && (
          <div id="documentUploadPreview" className="upload-preview">
            {file ? <DocumentPreviewBody url={fileUrl} status="ready" type={previewType} name={previewName} compact /> : <StoredPreview document={document!} />}
          </div>
        )}
        <p className={feedback.warning ? 'form-error' : 'form-help'} id="uploadDateScanFeedback" aria-live="polite">{feedback.text}</p>
        <div className="form-actions"><button className="btn btn-primary" id="saveDocumentUpload" type="submit">{document ? 'Save document' : 'Upload document'}</button></div>
      </form>
    </Modal>
  );
}

function StoredPreview({ document }: { document: ComplianceDocument }) {
  const { url, status } = useDocumentFile(document);
  return <DocumentPreviewBody url={url} status={status} type={document.type} name={document.name} compact />;
}
